import React, { ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ObjectShape } from 'yup/lib/object';
import * as yup from 'yup';
import { DialogFooter, ElevatedButton, TextButton, TextInput } from '@producer/ui-kit';

import { TextAreaInput } from '../textarea-input/TextAreaInput';
import { FieldContainer, FormTitle, HasServerResponse } from '../PanelContainer';
import { Alert } from '../alert/Alert';
import { ModalHeader } from './modal-header';
import { FormBuilderDefinition, type FormBuilderField } from './form-builder';
import { ProfileAvatar } from '../profile-avatar/ProfileAvatar';
import { useResponsive } from '../../hooks/useResponsive';

import styles from './form-builder-modal.module.scss';
interface FormBuilderModalProps {
  model: 'location' | 'contact' | 'character';
  definition: FormBuilderDefinition;
  onSubmit: (values: any) => Promise<void>;
  onCancel: () => void;
  autoFocus?: boolean;
  initialValues?: any;
}

export const FormBuilderModal: React.FC<FormBuilderModalProps> = ({
  model,
  definition,
  onSubmit,
  onCancel,
  initialValues,
}) => {
  const t = useTranslation(model);
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState<any>();
  const { isDesktop } = useResponsive();

  const onHandleSubmit = async (values: any) => {
    if (isLoading) {
      return;
    }

    setIsLoading(true);

    try {
      await onSubmit(values);
      handleCancel();
    } catch (e) {
      setServerError(e);
    } finally {
      setIsLoading(false);
    }
  };

  const buildValidationSchema = (fields: FormBuilderField[]) => {
    const schema: ObjectShape = {};

    for (const field of fields) {
      if (field.validation) {
        schema[field.name!] = field.validation;
      } else if (field.fields?.length) {
        schema[field.name!] = buildValidationSchema(field.fields);
      }
    }

    return yup.object().shape(schema);
  };

  const schema = useMemo(() => buildValidationSchema(definition.fields), [t]);

  // TODO: Works just with two levels of hierarchy
  const getFieldName = (field: FormBuilderField, parentFieldName?: string) => {
    return parentFieldName ? `${parentFieldName}.${field.name}` : field.name;
  };

  const getFieldDefaultValue = (field: FormBuilderField) => {
    return (
      initialValues?.[field.name!] || (['text', 'textArea'].includes(field.type) ? '' : undefined)
    );
  };

  const buildDefaultValues = (fields: FormBuilderField[]) => {
    const defaultValues: Record<string, any> = {};

    for (const field of fields) {
      defaultValues[field.name!] = getFieldDefaultValue(field);
    }

    return defaultValues;
  };

  const renderForm = (fields: FormBuilderField[], parentFieldName?: string): ReactNode[] => {
    return fields.map((field) => {
      if (field.type === 'group') {
        return (
          <>
            <FormTitle>{field.sectionHeader}</FormTitle>
            {field.fields?.length && renderForm(field.fields!, field.name)}
          </>
        );
      }

      if (field.type === 'upload') {
        return (
          <div className={styles['profile-avatar-wrapper']} key={field.name}>
            <ProfileAvatar
              placeholder="image"
              defaultUrl={initialValues?.avatar}
              onChange={field.handleSetImage}
            />
          </div>
        );
      } else if (field.type === 'text') {
        return (
          <FieldContainer key={field.name}>
            <TextInput
              {...register(getFieldName(field, parentFieldName)!)}
              placeholder={field.placeholder}
              errorMessage={errors[field.name!]?.message as string}
              label={field.label}
              defaultValue={getFieldDefaultValue(field)}
              required={!!field.isRequired}
            />
          </FieldContainer>
        );
      } else if (field.type === 'textArea') {
        return (
          <FieldContainer key={field.name}>
            <TextAreaInput
              {...register(getFieldName(field, parentFieldName)!)}
              placeholder={field.placeholder}
              errorMessage={errors[field.name!]?.message}
              label={field.label}
              required={!!field.isRequired}
            />
          </FieldContainer>
        );
      } else if (field.type === 'controller') {
        return (
          <FieldContainer key={field.name}>
            <label className={styles['label']}>{field.label}</label>
            <Controller
              control={control}
              name={getFieldName(field, parentFieldName)! as any}
              render={field.render!}
            />
          </FieldContainer>
        );
      } else {
        return <></>;
      }
    });
  };

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: buildDefaultValues(definition.fields),
  });

  const handleCancel = () => {
    reset();
    onCancel();
  };

  return (
    <div className={`${styles['content']} DialogContent`}>
      <ModalHeader
        title={definition.header}
        onClose={handleCancel}
        action={initialValues ? 'Update' : 'Create'}
        onAction={handleSubmit(onHandleSubmit)}
      />

      <div className={styles['container']}>
        {serverError && serverError.message && (
          <HasServerResponse>
            <Alert text={serverError.message} />
          </HasServerResponse>
        )}

        <div className={styles['form']}>{renderForm(definition.fields)}</div>
      </div>

      {isDesktop && (
        <DialogFooter
          actions={[
            <TextButton key="cancel" text="Cancel" onClick={handleCancel} />,
            <ElevatedButton
              key="submit"
              id={`${model}-submit`}
              text={definition.submitText}
              isLoading={isLoading}
              onClick={handleSubmit(onHandleSubmit)}
            />,
          ]}
        />
      )}
    </div>
  );
};
